import React, { useState } from 'react';
import axios from 'axios';
import {
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Button,
  Box,
  TextField,
  CircularProgress,
  Slide,
  Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const API_URL = process.env.REACT_APP_API_URL;

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const labels = {
  th: {
    title: 'แก้ไขกิจกรรม', save: 'บันทึก',
    eventName: 'ชื่อกิจกรรม', location: 'สถานที่', description: 'รายละเอียด',
    summary: 'สรุป', startDate: 'วันเริ่มต้น', endDate: 'วันสิ้นสุด',
    imageLink: 'ลิงก์รูปภาพ', website: 'เว็บไซต์',
    status: 'สถานะ', language: 'ภาษา'
  },
  en: {
    title: 'Edit Event', save: 'Save',
    eventName: 'Event Name', location: 'Location', description: 'Description',
    summary: 'Summary', startDate: 'Start Date', endDate: 'End Date',
    imageLink: 'Image Link', website: 'Website',
    status: 'Status', language: 'Language'
  },
  zh: {
    title: '编辑活动', save: '保存',
    eventName: '活动名称', location: '地点', description: '描述',
    summary: '摘要', startDate: '开始日期', endDate: '结束日期',
    imageLink: '图片链接', website: '网站',
    status: '状态', language: '语言'
  }
};

// Format datetime-local input value from ISO string
const toDatetimeLocal = (isoStr) => {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  if (isNaN(d)) return '';
  // Format: YYYY-MM-DDTHH:mm
  return d.toISOString().slice(0, 16);
};

const EditEventModal = ({ open, event, onClose, onSaved, language = 'th' }) => {
  const l = labels[language] || labels.en;

  const [form, setForm] = useState({
    eventName: event?.event_name || '',
    eventLocation: event?.event_location || '',
    eventDescription: event?.event_description || '',
    eventSummary: event?.event_summary || '',
    eventDate: toDatetimeLocal(event?.event_date),
    eventEndDate: toDatetimeLocal(event?.event_end_date),
    eventImageLink: event?.event_image_link || '',
    websiteLink: event?.website_link || ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (field) => (e) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await axios.put(
        `${API_URL}/events/${event.id}`,
        {
          eventName: form.eventName,
          eventLocation: form.eventLocation,
          eventDescription: form.eventDescription,
          eventSummary: form.eventSummary,
          eventDate: form.eventDate || null,
          eventEndDate: form.eventEndDate || null,
          eventImageLink: form.eventImageLink,
          websiteLink: form.websiteLink,
          language: event?.language,
          status: event?.status,
          latitude: event?.latitude,
          longitude: event?.longitude,
          eventCreatorId: event?.event_creator_id
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      onSaved && onSaved(res.data.event || res.data);
    } catch (err) {
      console.error('❌ [EditEventModal] Save error:', err);
      setError(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog fullScreen open={open} onClose={onClose} TransitionComponent={Transition}>
      <AppBar sx={{ position: 'relative' }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6">
            {l.title}
          </Typography>
          <Button color="inherit" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} color="inherit" /> : l.save}
          </Button>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 3, maxWidth: 720, mx: 'auto', width: '100%' }}>
        {error && (
          <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>
        )}

        {/* Read-only info chips */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          {event?.status && <Chip label={`${l.status}: ${event.status}`} size="small" />}
          {event?.language && <Chip label={`${l.language}: ${event.language}`} size="small" />}
        </Box>

        <TextField
          label={l.eventName}
          value={form.eventName}
          onChange={handleChange('eventName')}
          fullWidth
          sx={{ mb: 2 }}
        />

        <TextField
          label={l.location}
          value={form.eventLocation}
          onChange={handleChange('eventLocation')}
          fullWidth
          sx={{ mb: 2 }}
        />

        <TextField
          label={l.summary}
          value={form.eventSummary}
          onChange={handleChange('eventSummary')}
          fullWidth
          sx={{ mb: 2 }}
        />

        <TextField
          label={l.description}
          value={form.eventDescription}
          onChange={handleChange('eventDescription')}
          fullWidth
          multiline
          minRows={5}
          sx={{ mb: 2 }}
        />

        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <TextField
            label={l.startDate}
            type="datetime-local"
            value={form.eventDate}
            onChange={handleChange('eventDate')}
            InputLabelProps={{ shrink: true }}
            sx={{ flex: 1, minWidth: 220 }}
          />
          <TextField
            label={l.endDate}
            type="datetime-local"
            value={form.eventEndDate}
            onChange={handleChange('eventEndDate')}
            InputLabelProps={{ shrink: true }}
            sx={{ flex: 1, minWidth: 220 }}
          />
        </Box>

        <TextField
          label={l.imageLink}
          value={form.eventImageLink}
          onChange={handleChange('eventImageLink')}
          fullWidth
          sx={{ mb: 2 }}
        />

        <TextField
          label={l.website}
          value={form.websiteLink}
          onChange={handleChange('websiteLink')}
          fullWidth
          sx={{ mb: 2 }}
        />
      </Box>
    </Dialog>
  );
};

export default EditEventModal;
